// Sudoku API Service
// API Documentation: https://sudoku-api.vercel.app/

export interface SudokuApiResponse {
  newboard: {
    grids: Array<{
      value: number[][];
      solution: number[][];
      difficulty: string;
    }>;
    results: number;
    message: string;
  };
}

export interface SudokuPuzzle {
  puzzle: number[][];
  solution: number[][];
  difficulty: string;
}

// Map API difficulties to our app difficulties
const DIFFICULTY_MAPPING: { [key: string]: string } = {
  'Easy': 'Easy',
  'Medium': 'Medium', 
  'Hard': 'Hard',
  'Expert': 'Expert',
  'Master': 'Master',
  'Extreme': 'Extreme'
};

// Reverse mapping for API requests
const API_DIFFICULTY_MAPPING: { [key: string]: string } = {
  'Easy': 'Easy',
  'Medium': 'Medium',
  'Hard': 'Hard',
};

export class SudokuApiService {
  private static readonly BASE_URL = 'https://sudoku-api.vercel.app/api/dosuku';
  
  /**
   * Fetch a new Sudoku puzzle from the API
   * @param difficulty - The difficulty level (Easy, Medium, Hard, Expert, Master, Extreme)
   * @returns Promise<SudokuPuzzle>
   */
  static async fetchPuzzle(difficulty: string = 'Medium'): Promise<SudokuPuzzle> {
    try {
      // Map our difficulty to API difficulty
      const apiDifficulty = API_DIFFICULTY_MAPPING[difficulty] || 'Medium';
      
      // GraphQL query to get puzzle, solution, and difficulty
      const query = `{newboard(limit:1){grids{value,solution,difficulty}}}`;
      const url = `${this.BASE_URL}?query=${encodeURIComponent(query)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: SudokuApiResponse = await response.json();
      
      if (!data.newboard || !data.newboard.grids || data.newboard.grids.length === 0) {
        throw new Error('Invalid API response structure');
      }

      const grid = data.newboard.grids[0];
      
      // Validate the grid structure
      if (!grid.value || !grid.solution || !Array.isArray(grid.value) || !Array.isArray(grid.solution)) {
        throw new Error('Invalid grid structure from API');
      }

      // Ensure 9x9 grid
      if (grid.value.length !== 9 || grid.solution.length !== 9) {
        throw new Error('Invalid grid size from API');
      }

      return {
        puzzle: grid.value,
        solution: grid.solution,
        difficulty: DIFFICULTY_MAPPING[grid.difficulty] || difficulty
      };

    } catch (error) {
      
      // Fallback to local generation if API fails
      return this.generateFallbackPuzzle(difficulty);
    }
  }

  /**
   * Fallback puzzle generation when API fails
   * This creates a simple puzzle for testing purposes
   */
  private static generateFallbackPuzzle(difficulty: string): SudokuPuzzle {
    
    // Simple fallback puzzle (Easy level)
    const solution = [
      [5, 3, 4, 6, 7, 8, 9, 1, 2],
      [6, 7, 2, 1, 9, 5, 3, 4, 8],
      [1, 9, 8, 3, 4, 2, 5, 6, 7],
      [8, 5, 9, 7, 6, 1, 4, 2, 3],
      [4, 2, 6, 8, 5, 3, 7, 9, 1],
      [7, 1, 3, 9, 2, 4, 8, 5, 6],
      [9, 6, 1, 5, 3, 7, 2, 8, 4],
      [2, 8, 7, 4, 1, 9, 6, 3, 5],
      [3, 4, 5, 2, 8, 6, 1, 7, 9]
    ];

    // Create puzzle by removing numbers based on difficulty
    const puzzle = solution.map(row => [...row]);
    const cellsToRemove = this.getCellsToRemoveForDifficulty(difficulty);
    
    // Remove random cells
    const positions = [];
    for (let i = 0; i < 9; i++) {
      for (let j = 0; j < 9; j++) {
        positions.push({ row: i, col: j });
      }
    }
    
    // Shuffle positions
    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }
    
    // Remove cells
    for (let i = 0; i < cellsToRemove && i < positions.length; i++) {
      const { row, col } = positions[i];
      puzzle[row][col] = 0;
    }

    return {
      puzzle,
      solution,
      difficulty
    };
  }

  /**
   * Get number of cells to remove based on difficulty
   */
  private static getCellsToRemoveForDifficulty(difficulty: string): number {
    const cellsToRemove: { [key: string]: number } = {
      'Easy': 36,      // 45 clues remaining
      'Medium': 46,    // 35 clues remaining  
      'Hard': 53,      // 28 clues remaining
      'Expert': 59,    // 22 clues remaining
      'Master': 63,    // 18 clues remaining
      'Extreme': 66    // 15 clues remaining
    };
    
    return cellsToRemove[difficulty] || cellsToRemove['Medium'];
  }

  /**
   * Validate if a number can be placed in a specific position
   */
  static isValidMove(board: number[][], row: number, col: number, num: number): boolean {
    // Check row
    for (let x = 0; x < 9; x++) {
      if (board[row][x] === num) return false;
    }
    
    // Check column
    for (let x = 0; x < 9; x++) {
      if (board[x][col] === num) return false;
    }
    
    // Check 3x3 box
    const startRow = row - (row % 3);
    const startCol = col - (col % 3);
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (board[i + startRow][j + startCol] === num) return false;
      }
    }
    
    return true;
  }

  /**
   * Check if the puzzle is completed correctly
   */
  static isPuzzleComplete(board: number[][]): boolean {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (board[row][col] === 0) return false;
      }
    }
    return true;
  }

  /**
   * Get available difficulty levels
   */
  static getAvailableDifficulties(): string[] {
    return ['Easy', 'Medium', 'Hard', 'Expert', 'Master', 'Extreme'];
  }
}
